import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { useLanguageStore } from '@/modules/core';

import {
  popoverContentVariants,
  popoverArrowVariants,
  popoverCloseVariants,
} from './Popover.styles';

import type {
  PopoverProps,
  PopoverTriggerProps,
  PopoverContentProps,
  PopoverArrowProps,
  PopoverCloseProps,
} from './Popover.types';

/**
 * Popover Root Component
 *
 * Container for all popover parts. Manages open/close state.
 * Use this to wrap trigger and content.
 *
 * @example
 * ```tsx
 * <Popover open={isOpen} onOpenChange={setIsOpen}>
 *   <PopoverTrigger>Click me</PopoverTrigger>
 *   <PopoverContent>Content here</PopoverContent>
 * </Popover>
 * ```
 */
export const Popover: React.FC<PopoverProps> = ({ children, ...props }) => {
  const { isRTL } = useLanguageStore();
  const Component = PopoverPrimitive.Root as React.ComponentType<
    PopoverProps & { dir?: 'ltr' | 'rtl' }
  >;
  return (
    <Component dir={isRTL ? 'rtl' : 'ltr'} {...props}>
      {children}
    </Component>
  );
};

Popover.displayName = 'Popover';

/**
 * PopoverTrigger Component
 *
 * Button that opens the popover when clicked.
 * Can render as its child using asChild prop.
 *
 * @example
 * ```tsx
 * <PopoverTrigger>Open</PopoverTrigger>
 * // OR with custom element
 * <PopoverTrigger asChild>
 *   <Button>Open</Button>
 * </PopoverTrigger>
 * ```
 */
export const PopoverTrigger = React.forwardRef<HTMLButtonElement, PopoverTriggerProps>(
  ({ className, children, ...props }, ref) => (
    <PopoverPrimitive.Trigger ref={ref} className={cn(className)} {...props}>
      {children}
    </PopoverPrimitive.Trigger>
  )
);

PopoverTrigger.displayName = 'PopoverTrigger';

/**
 * PopoverAnchor Component
 *
 * Positions the content against something other than the trigger — a child
 * element, or a `virtualRef` to any measurable element elsewhere in the page.
 */
export const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * PopoverContent Component
 *
 * The popover panel that appears when triggered.
 * Contains the main content and handles positioning.
 *
 * @example
 * ```tsx
 * <PopoverContent side="bottom" align="start">
 *   <p>Popover content</p>
 * </PopoverContent>
 * ```
 */
export const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  (
    {
      className,
      children,
      side = 'bottom',
      align = 'center',
      sideOffset = 4,
      alignOffset = 0,
      showArrow = false,
      avoidCollisions = true,
      scale = 'm',
      ...props
    },
    ref
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        side={side}
        align={align}
        sideOffset={sideOffset}
        alignOffset={alignOffset}
        avoidCollisions={avoidCollisions}
        className={cn(popoverContentVariants({ scale }), className)}
        {...props}
      >
        {children}
        {showArrow && <PopoverArrow />}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
);

PopoverContent.displayName = 'PopoverContent';

/**
 * PopoverArrow Component
 *
 * Optional arrow that points to the trigger element.
 * Automatically positioned by Radix based on popover placement.
 * Simple white triangle matching the popover background (14px x 7px from Figma).
 *
 * @example
 * ```tsx
 * <PopoverContent>
 *   Content
 *   <PopoverArrow />
 * </PopoverContent>
 * ```
 */
export const PopoverArrow = React.forwardRef<SVGSVGElement, PopoverArrowProps>(
  ({ className, width = 14, height = 7, ...props }, ref) => (
    <PopoverPrimitive.Arrow
      ref={ref}
      width={width}
      height={height}
      className={cn(popoverArrowVariants(), className)}
      {...props}
    />
  )
);

PopoverArrow.displayName = 'PopoverArrow';

/**
 * PopoverClose Component
 *
 * Button that closes the popover when clicked.
 * Can be placed anywhere inside PopoverContent.
 *
 * @example
 * ```tsx
 * <PopoverContent>
 *   <p>Content</p>
 *   <PopoverClose>Close</PopoverClose>
 * </PopoverContent>
 * ```
 */
export const PopoverClose = React.forwardRef<HTMLButtonElement, PopoverCloseProps>(
  ({ className, children, ...props }, ref) => (
    <PopoverPrimitive.Close ref={ref} className={cn(popoverCloseVariants(), className)} {...props}>
      {children}
    </PopoverPrimitive.Close>
  )
);

PopoverClose.displayName = 'PopoverClose';
